import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MarketDataService from "../../services/market/MarketDataService";
import {
  getAccessToken,
  subscribeToAuthTokenChanges,
} from "../../services/platform/api";
import { WS_BASE_URL } from "../../services/platform/apiConfig";
import {
  selectIsLoggedIn,
  selectSocketStatus,
  selectUser,
  setInitialized,
  setIsLoggedIn,
  setUser,
} from "../../redux/features/platformSlice";
import SocketDisconnectedModal from "./SocketDisconnectedModal";

const InitialSocketConnection = () => {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);
  const user = useSelector(selectUser);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const socketStatus = useSelector(selectSocketStatus);
  const userLogin = user?.login;
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const visibilityRef = useRef(document.visibilityState);
  const connectionAttemptedRef = useRef(false);
  const connectionRef = useRef({ url: null, token: null });

  const connectSocket = useCallback((force = false) => {
    if (!isLoggedIn || !userLogin) return;

    const token = getAccessToken();
    if (!token) {
      MarketDataService.disconnect();
      connectionRef.current = { url: null, token: null };
      return;
    }

    const changed =
      connectionRef.current.url !== WS_BASE_URL ||
      connectionRef.current.token !== token;

    if (force || changed || !MarketDataService.isConnected) {
      connectionAttemptedRef.current = true;
      MarketDataService.connect(WS_BASE_URL, token, {
        prices: true,
        force,
      });
      connectionRef.current = { url: WS_BASE_URL, token };
    }
  }, [isLoggedIn, userLogin]);

  useEffect(() => {
    if (!isLoggedIn) {
      setShowDisconnectModal(false);
      return;
    }
    if (socketStatus === "connected") {
      setShowDisconnectModal(false);
    } else if (
      socketStatus === "error" ||
      socketStatus === "reconnecting" ||
      (socketStatus === "disconnected" && connectionAttemptedRef.current)
    ) {
      setShowDisconnectModal(true);
    }
  }, [isLoggedIn, socketStatus]);

  useEffect(() => {
    if (!isLoggedIn || !userLogin) {
      connectionAttemptedRef.current = false;
      MarketDataService.disconnect();
      connectionRef.current = { url: null, token: null };
      return undefined;
    }

    connectSocket();

    const unsubscribeAuth = subscribeToAuthTokenChanges(() => {
      if (!getAccessToken()) {
        MarketDataService.disconnect();
        dispatch(setUser(null));
        dispatch(setIsLoggedIn(false));
        dispatch(setInitialized(false));
        return;
      }
      connectSocket(true);
    });
    const handleOnline = () => connectSocket(true);
    const handleVisibility = () => {
      if (
        visibilityRef.current === "hidden" &&
        document.visibilityState === "visible"
      ) {
        connectSocket();
      }
      visibilityRef.current = document.visibilityState;
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      unsubscribeAuth();
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      MarketDataService.disconnect();
      connectionRef.current = { url: null, token: null };
    };
  }, [connectSocket, dispatch, isLoggedIn, userLogin]);

  useEffect(() => {
    const readyTimeout = setTimeout(() => {
      setReady(true);
    }, 1000 * 60); // 1 minute

    return () => clearTimeout(readyTimeout);
  }, []);

  if (!showDisconnectModal) return null;

  if (!ready) return null;

  return (
    <SocketDisconnectedModal
      status={socketStatus}
      onReconnect={() => connectSocket(true)}
    />
  );
};

export default memo(InitialSocketConnection);
